import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload } from "lucide-react";

interface Favicon {
  id: string;
  name: string;
  image_url: string;
  is_active: boolean;
}

interface FormData {
  name: string;
}

interface FaviconManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  favicon?: Favicon | null;
}

export function FaviconManagementDialog({
  open,
  onOpenChange,
  favicon,
}: FaviconManagementDialogProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      name: "",
    },
  });

  useEffect(() => {
    if (favicon) {
      reset({ name: favicon.name });
      setUploadedUrl(favicon.image_url);
    } else {
      reset({ name: "" });
      setUploadedUrl(null);
    }
  }, [favicon, reset]);

  const saveMutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (!uploadedUrl) {
        throw new Error("Please upload a favicon image");
      }

      if (favicon) {
        const { error } = await supabase
          .from("favicons")
          .update({
            name: data.name,
            image_url: uploadedUrl,
          })
          .eq("id", favicon.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("favicons")
          .insert({
            name: data.name,
            image_url: uploadedUrl,
            is_active: false,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-favicons"] });
      toast.success(favicon ? "Favicon updated" : "Favicon added");
      onOpenChange(false);
      reset();
      setUploadedUrl(null);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/png", "image/x-icon", "image/ico", "image/vnd.microsoft.icon", "image/svg+xml"];
    if (!validTypes.includes(file.type) && !file.name.endsWith(".ico")) {
      toast.error("Please upload a PNG, ICO, or SVG file");
      return;
    }

    // Validate file size (max 500KB for favicon)
    if (file.size > 500 * 1024) {
      toast.error("File size must be less than 500KB");
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("favicons")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("favicons")
        .getPublicUrl(fileName);

      setUploadedUrl(publicUrl);
      toast.success("Favicon uploaded");
    } catch (error) {
      toast.error("Failed to upload favicon");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = (data: FormData) => {
    saveMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{favicon ? "Edit Favicon" : "Add Favicon"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="e.g., Default, Holiday, Event"
              {...register("name", { required: "Name is required" })}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Favicon Image</Label>
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="outline"
                disabled={uploading}
                onClick={() => document.getElementById("favicon-upload")?.click()}
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Upload
              </Button>
              <input
                id="favicon-upload"
                type="file"
                accept=".ico,.png,.svg"
                className="hidden"
                onChange={handleFileUpload}
              />
              {uploadedUrl && (
                <div className="flex items-center gap-2">
                  <img
                    src={uploadedUrl}
                    alt="Favicon preview"
                    className="h-8 w-8 object-contain border rounded"
                  />
                  <span className="text-sm text-muted-foreground">Uploaded</span>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Recommended: 32x32 or 64x64 PNG, ICO, or SVG. Max 500KB.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saveMutation.isPending || !uploadedUrl}>
              {saveMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {favicon ? "Update" : "Add"} Favicon
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
